/**
 * Feedback Feature — TypeScript Interfaces
 *
 * Data model for the voice feedback capture feature (FS-009).
 * Supports recording voice comments, screen context capture,
 * transcription, and GitHub issue creation.
 */

// ---------------------------------------------------------------------------
// Screen Capture
// ---------------------------------------------------------------------------

export interface ScreenCapture {
  /** URL path: e.g., "/dashboard", "/onboarding" */
  readonly route: string;

  /** document.title at capture time */
  readonly pageTitle: string;

  /** Serialized HTML of the main content area (excluding feedback widget) */
  readonly htmlSnapshot: string;

  /** Viewport dimensions at capture time */
  readonly viewport: { width: number; height: number };

  /** ISO 8601 timestamp */
  readonly capturedAt: string;
}

// ---------------------------------------------------------------------------
// Voice Comment
// ---------------------------------------------------------------------------

export interface VoiceComment {
  /** Sequential within session: 1, 2, 3… */
  readonly index: number;

  /** Audio blob serialized as base64 data URL for IndexedDB persistence */
  audioDataUrl: string | null;

  /** MIME type of the audio blob */
  audioMimeType: string;

  /** Audio duration in seconds */
  readonly durationSeconds: number;

  /** Transcribed text (editable by user) */
  transcription: string;

  /** Auto-generated via "end title" keyword or first sentence */
  title: string;

  /** LLM-generated max-10-word summary of the recording (null until generated) */
  summary: string | null;

  /** LLM-generated paraphrase of the transcription for use in aggregation (null until generated) */
  paraphrase: string | null;

  /** Screens captured during this recording */
  readonly screenCaptures: ScreenCapture[];

  /** Transcription status */
  transcriptionStatus: "pending" | "transcribing" | "complete" | "failed";

  /** ISO 8601 timestamp */
  readonly recordedAt: string;
}

// ---------------------------------------------------------------------------
// Feedback Session
// ---------------------------------------------------------------------------

export type FeedbackSessionStatus = "active" | "saving" | "saved" | "error";

export interface FeedbackSession {
  /** Auto-generated: "feedback-YYYY-MM-DD-HHhMM" */
  sessionName: string;

  /** ISO 8601 timestamp of session start */
  readonly startedAt: string;

  /** Ordered list of voice comments */
  comments: VoiceComment[];

  /** Current session state */
  status: FeedbackSessionStatus;
}

// ---------------------------------------------------------------------------
// GitHub Configuration
// ---------------------------------------------------------------------------

// GitHubConfig has been removed — GitHub PAT, owner, and repo are now
// managed server-side by Cloud Functions (feedbackCreateIssue, feedbackPostComment).

// ---------------------------------------------------------------------------
// Rate Limit State (persisted to localStorage)
// ---------------------------------------------------------------------------

export interface RateLimitState {
  /** Timestamps of issue creation calls (ISO 8601) */
  issueTimestamps: string[];

  /** Timestamps of session saves (ISO 8601) */
  sessionTimestamps: string[];

  /** Cooldown expiry (ISO 8601), null if not in cooldown */
  cooldownUntil: string | null;

  /** HMAC for tamper detection */
  hmac: string;
}

// ---------------------------------------------------------------------------
// Feedback Category
// ---------------------------------------------------------------------------

/** Categories of feedback that can be submitted. */
export type FeedbackCategory =
  | "bug"
  | "kudos"
  | "feature-request"
  | "docs-bug"
  | "usability"
  | "performance";

/** Human-readable labels for feedback categories. */
export const FEEDBACK_CATEGORY_LABELS: Record<FeedbackCategory, string> = {
  bug: "Bug Report",
  kudos: "Kudos / Praise",
  "feature-request": "Feature Request",
  "docs-bug": "Documentation Issue",
  usability: "Usability Issue",
  performance: "Performance Issue",
};

// ---------------------------------------------------------------------------
// Environment Information
// ---------------------------------------------------------------------------

/** Runtime environment information captured at feedback submission time. */
export interface EnvironmentInfo {
  /** Whether the user is on mobile or web */
  readonly deviceType: "mobile" | "web";

  /** Platform details: e.g. "Android 14", "iOS 17.4", "Chrome 120" */
  readonly platform: string;

  /** URL domain: e.g. "pr-400.retire-ez.pages.dev", "dev.retire-ez.ca" */
  readonly urlDomain: string;
}

// ---------------------------------------------------------------------------
// Issue Proposal (LLM-aggregated)
// ---------------------------------------------------------------------------

/** A proposed GitHub issue aggregated from one or more voice comments. */
export interface IssueProposal {
  /** LLM-generated issue title */
  title: string;

  /** LLM-generated paraphrased description */
  description: string;

  /** LLM-generated next-step suggestions */
  suggestions: string[];

  /** LLM-generated triage summary */
  triageSummary: string;

  /** Labels for the GitHub issue */
  labels: string[];

  /** Feedback category for display purposes */
  category: FeedbackCategory;

  /** Indices of VoiceComments that contributed to this proposal */
  readonly sourceCommentIndices: number[];
}

// ---------------------------------------------------------------------------
// Issue Creation Results
// ---------------------------------------------------------------------------

export interface IssueResult {
  readonly success: boolean;
  readonly issueNumber?: number;
  readonly issueUrl?: string;
  readonly error?: string;
}

export interface SaveResult {
  readonly totalComments: number;
  readonly issuesCreated: number;
  readonly failures: IssueResult[];
  readonly results: IssueResult[];
}

// ---------------------------------------------------------------------------
// Feedback Store State
// ---------------------------------------------------------------------------

export interface FeedbackStoreState {
  /** Current session (null when idle) */
  session: FeedbackSession | null;

  /** Whether the panel is open */
  panelOpen: boolean;

  /** Whether currently recording */
  isRecording: boolean;

  /** Live interim transcription text */
  interimTranscript: string;

  /** Recording duration in seconds (live counter) */
  recordingDuration: number;

  /** Last save result */
  lastSaveResult: SaveResult | null;
}

export interface FeedbackStoreActions {
  /** Start a new session */
  startSession: () => void;

  /** End the current session */
  endSession: () => void;

  /** Toggle panel open/closed */
  togglePanel: () => void;

  /** Set panel state directly */
  setPanelOpen: (open: boolean) => void;

  /** Add a completed voice comment */
  addComment: (comment: VoiceComment) => void;

  /** Delete a comment by index */
  deleteComment: (index: number) => void;

  /** Update a comment's transcription text */
  updateTranscription: (index: number, text: string) => void;

  /** Update a comment's title */
  updateTitle: (index: number, title: string) => void;

  /** Set a comment's LLM summary */
  setCommentSummary: (index: number, summary: string) => void;

  /** Set a comment's LLM-generated paraphrase */
  setCommentParaphrase: (index: number, paraphrase: string) => void;

  /** Set recording state */
  setRecording: (recording: boolean) => void;

  /** Set interim transcript */
  setInterimTranscript: (text: string) => void;

  /** Set recording duration */
  setRecordingDuration: (seconds: number) => void;

  /** Set session status */
  setSessionStatus: (status: FeedbackSessionStatus) => void;

  /** Set session name */
  setSessionName: (name: string) => void;

  /** Set last save result */
  setLastSaveResult: (result: SaveResult | null) => void;

  /** Persist session to IndexedDB */
  persistSession: () => Promise<void>;

  /** Restore session from IndexedDB */
  restoreSession: () => Promise<void>;

  /** Clear persisted session from IndexedDB */
  clearPersistedSession: () => Promise<void>;
}

export type FeedbackStore = FeedbackStoreState & FeedbackStoreActions;
